<template>
  <v-card id="portfolio-end-of-year" class="px-4 pt-2 pb-4 my-0 elevation-0">
    <v-card-title class="px-1">
      <h1 class="card-title mb-1">
        {{ title || $t(`views.tax.taxSummary.portfolioEoy.title`) }} {{ year }}
      </h1>
      <v-spacer />
      <template v-if="showShowCashButton">
        <ShowCashButton class="mt-2" :showCash="showCash" @showCashChanged="showCashChanged" />
      </template>
      <ExportButton
        class="mt-2"
        :click="downloadCsv"
        :loading="exportLoading"
        :disabled="!result.length"
      />
    </v-card-title>
    <v-card-text class="mt-0 pa-0 pt-3">
      <v-chip-group v-model="brokerSelected" active-class="primary" mandatory class="ml-3" column>
        <v-chip v-for="broker in brokers" :key="broker._id" :value="broker._id" outlined>
          {{ broker.alias }} ({{ broker.country }})
        </v-chip>
      </v-chip-group>

      <v-data-table
        id="positions-data-table"
        :headers="headers"
        :items="result"
        class="mt-3 elevation-0"
        mobile-breakpoint="0"
        :page.sync="currentPage"
        :loading="loading"
        :footer-props="{
          'items-per-page-options': [5, 10, 15, 20, 30, 50, 100],
        }"
        :items-per-page="itemsPerPage"
        :dense="user.preferences.denseTables"
      >
        <template slot="body.prepend">
          <tr v-if="totalRow" class="row-total">
            <td>{{ $t(`views.tax.taxSummary.portfolioEoy.total`) }}</td>
            <td v-if="showKeyType"></td>
            <td v-if="showIsin"></td>
            <td v-if="showCountry"></td>
            <td v-if="showLegalCountry"></td>
            <td></td>
            <td></td>
            <td v-if="showAvgLast3Months && showCash"></td>
            <td></td>
            <td class="text-right">
              <currency
                v-if="totalRow && totalRow.totalValueBaseCurrency"
                :value="totalRow.totalValueBaseCurrency"
                :currency="user.baseCurrency"
                strong
                :hideDataNoPlanUser="hideDataNoPlanUser"
              />
              <span v-else></span>
            </td>
          </tr>
        </template>

        <template v-slot:item.position.ticker="{ item }">
          <TableTicker :position="item.position" notLinkToDetail />
        </template>

        <template v-slot:item.keyType="{ item }">
          <span v-if="item.keyType">
            {{ $t(`views.tax.taxSummary.portfolioEoy.keyTypes.${item.keyType}`) }}
          </span>
        </template>

        <template v-slot:item.position.isin="{ item }">
          <span v-if="item.position.isin">{{ item.position.isin }}</span>
          <span v-else-if="['cash', 'crypto', 'commodity'].includes(item.position.type)">-</span>
          <span v-else-if="item.position.type === 'fund'">{{ item.position.ticker }}</span>
          <div v-else>
            <InfoIcon
              :text="$t('views.tax.taxSummary.portfolioEoy.infoIsin')"
              color="info"
              :size="17"
            />
          </div>
        </template>

        <template v-slot:item.position.country="{ item }">
          <span v-if="['cash', 'crypto', 'commodity'].includes(item.position.type)">-</span>
          <div v-else>
            {{
              $te(`common.countries.${item.position.country}`)
                ? $t(`common.countries.${item.position.country}`) +
                  ' (' +
                  item.position.country +
                  ')'
                : item.position.country
                ? item.position.country
                : '-'
            }}
          </div>
        </template>

        <template v-slot:item.position.legalCountry="{ item }">
          <span v-if="['cash', 'crypto', 'commodity'].includes(item.position.type)">-</span>
          <div v-else>
            {{
              $te(`common.countries.${item.position.legalCountry}`)
                ? $t(`common.countries.${item.position.legalCountry}`) +
                  ' (' +
                  item.position.legalCountry +
                  ')'
                : item.position.legalCountry
                ? item.position.legalCountry
                : '-'
            }}
          </div>
        </template>

        <template v-slot:item.origin="{ item }">
          <v-chip v-if="item.origin" small :color="originColor(item.origin)">
            {{ $t(`views.tax.taxSummary.portfolioEoy.origins.${item.origin}`) }}
          </v-chip>
        </template>

        <template v-slot:item.positionNumber="{ item }">
          <currency
            :value="item.positionNumber"
            :currency="item.position.type === 'cash' ? item.position.ticker : undefined"
            :decimals="item.position.type === 'crypto' ? 7 : 2"
          />
        </template>

        <template v-slot:item.totalValue="{ item }">
          <currency
            v-if="item.totalValue"
            :value="item.totalValue"
            :currency="item.position.currency"
            :hideDataNoPlanUser="hideDataNoPlanUser"
          />
          <span v-else></span>
        </template>

        <template v-slot:item.totalValueBaseCurrency="{ item }">
          <currency
            v-if="item.totalValueBaseCurrency"
            :value="item.totalValueBaseCurrency"
            :currency="user.baseCurrency"
            strong
            :hideDataNoPlanUser="hideDataNoPlanUser"
          />
          <span v-else></span>
        </template>

        <template v-slot:item.avgLast3MonthsBaseCurrency="{ item }">
          <currency
            v-if="item.avgLast3MonthsBaseCurrency != null"
            :value="item.avgLast3MonthsBaseCurrency"
            :currency="user.baseCurrency"
            :hideDataNoPlanUser="hideDataNoPlanUser"
          />
          <span v-else></span>
        </template>
      </v-data-table>
    </v-card-text>
  </v-card>
</template>

<script>
import { mapState } from 'vuex'
import { getPortfolioEndOfYear, getPortfolioEndOfYearCsv } from '@/lib/api.js'
import { getCurrencySymbol } from '@/lib/utils/currencies.js'
import { DateTime } from 'luxon'
import FileSaver from 'file-saver'
import Currency from '@/views/components/common/Currency.vue'
import TableTicker from '@/views/components/common/TableTicker'
import ShowCashButton from '@/views/components/common/ShowCashButton'
import InfoIcon from '@/views/components/common/InfoIcon'
import ExportButton from '@/views/components/common/ExportButton'

export default {
  components: { Currency, TableTicker, ShowCashButton, InfoIcon, ExportButton },
  props: {
    title: {
      type: String,
      default: undefined,
    },
    year: {
      type: String,
      required: true,
    },
    itemsPerPage: {
      type: Number,
      default: 10,
    },
    showKeyType: {
      type: Boolean,
      default: false,
    },
    showIsin: {
      type: Boolean,
      default: false,
    },
    showCountry: {
      type: Boolean,
      default: true,
    },
    showLegalCountry: {
      type: Boolean,
      default: true,
    },
    showCrypto: {
      type: Boolean,
      default: true,
    },
    showAvgLast3Months: {
      type: Boolean,
      default: true,
    },
    showShowCashButton: {
      type: Boolean,
      default: true,
    },
    positionTypes: {
      type: Array,
      default: () => [],
    },
    hideDataNoPlanUser: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      loading: false,
      exportLoading: false,
      resultData: null,
      result: [],
      showCash: true,
      totalRow: null,
      brokers: [],
      brokerSelected: null,
      currentPage: null,
    }
  },
  computed: {
    ...mapState(['user']),
    headers() {
      const headers = [
        {
          text: this.$t(`views.portfolio.positionsTable.ticker.all`),
          value: 'position.ticker',
        },
      ]
      if (this.showKeyType)
        headers.push({
          text: this.$t('views.tax.taxSummary.portfolioEoy.keyType'),
          value: 'keyType',
          sortable: true,
        })
      if (this.showIsin)
        headers.push({
          text: this.$t(`views.tax.taxSummary.portfolioEoy.isin`),
          value: 'position.isin',
          sortable: false,
        })
      if (this.showCountry)
        headers.push({
          text: this.$t(`common.country`),
          value: 'position.country',
        })
      if (this.showLegalCountry)
        headers.push({
          text: this.$t('views.stocks.stockTable.legalCountry'),
          value: 'position.legalCountry',
        })
      headers.push({
        text: this.$t('views.tax.taxSummary.portfolioEoy.origin'),
        value: 'origin',
        sortable: true,
      })

      headers.push({
        text: this.$t(`views.portfolio.positionsTable.positionNumber.all`),
        value: 'positionNumber',
        align: 'right',
      })
      if (this.showAvgLast3Months && this.showCash)
        headers.push({
          text: this.$t('views.tax.taxSummary.portfolioEoy.avgLast3Months'),
          value: 'avgLast3MonthsBaseCurrency',
          align: 'right',
        })
      headers.push(
        {
          text: `${this.$t('views.tax.taxSummary.portfolioEoy.totalValue')}`,
          value: 'totalValue',
          align: 'right',
        },
        {
          text: `${this.$t(
            'views.tax.taxSummary.portfolioEoy.totalValueBaseCurrency'
          )} (${getCurrencySymbol(this.user.baseCurrency)})`,
          value: 'totalValueBaseCurrency',
          align: 'right',
        }
      )

      return headers
    },
  },
  watch: {
    // year: {
    //   handler() {
    //     this.init()
    //   },
    //   immediate: true,
    // },
    brokerSelected() {
      this.setResult()
    },
  },
  methods: {
    originColor(origin) {
      const colors = {
        new: 'softBackgroundGreen',
        maintain: this.$vuetify.theme.dark ? 'dark_backgroundSoftBlue' : 'softPrimary',
        cancel: this.$vuetify.theme.dark ? 'dark_backgroundLighterBlack' : 'softBackgroundGrey',
      }
      return colors[origin] || 'softBackgroundGrey'
    },
    showCashChanged(showCash) {
      this.showCash = showCash
      this.init()
    },
    setResult() {
      if (!this.resultData) return
      if (!this.brokerSelected) {
        this.result = []
        this.totalRow = null
        return
      }

      const resultData = JSON.parse(JSON.stringify(this.resultData))
      const broker = resultData.byBrokerResult.find(
        (elem) => this.brokerSelected === elem.broker._id
      )
      if (!broker) return
      this.totalRow = broker.positions.shift()
      this.result = broker.positions

      this.currentPage = 1
    },
    getType() {
      let type
      if (this.positionTypes && this.positionTypes.length) {
        type = this.positionTypes
      } else {
        const $nin = []
        if (!this.showCash) $nin.push('cash')
        if (!this.showCrypto) $nin.push('crypto')

        if ($nin.length) {
          type = { $nin }
        }
      }
      return type
    },
    async init() {
      this.loading = true

      const type = this.getType()
      const result = await getPortfolioEndOfYear({
        year: this.year,
        positionFilter: { type },
      })
      this.resultData = result.data
      this.brokers = result.data.byBrokerResult.map((broker) => broker.broker)
      this.brokerSelected = this.brokers.length ? this.brokers[0]._id : null
      this.setResult()
      this.loading = false
    },
    async downloadCsv() {
      this.exportLoading = true
      const nowFormatted = DateTime.now().toFormat('yyyy_MM_dd HH_mm_ss')
      try {
        const type = this.getType()
        const result = await getPortfolioEndOfYearCsv({
          year: this.year,
          positionFilter: { type },
          broker: this.brokerSelected,
          columns: {
            showKeyType: this.showKeyType,
            showIsin: this.showIsin,
            showCountry: this.showCountry,
            showLegalCountry: this.showLegalCountry,
            showAvgLast3Months: this.showAvgLast3Months,
          },
        })
        const blob = new Blob([result.data], { type: 'text/csv;charset=utf-8' })
        const baseTitle = `${this.title || this.$t(`views.tax.taxSummary.portfolioEoy.title`)}${
          this.year
        }`
        const brokerSuffix = ` - ${
          this.brokers.find((b) => b._id === this.brokerSelected)?.alias || ''
        }`
        FileSaver.saveAs(blob, `Filios - ${baseTitle}${brokerSuffix} - ${nowFormatted}.csv`)
      } catch (error) {
        this.$root.$snackbar.checkAndOpenCommonLoadError(error)
      }

      this.exportLoading = false
    },
  },
}
</script>

<style lang="scss">
#portfolio-end-of-year {
  .row-total td {
    font-size: 17px !important;
    font-weight: 600 !important;
  }
}
</style>
